import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { IB_TOKEN_KEY, IB_USER_KEY, ibApi, type IbSubadmin } from './api'
import {
  clearAuthSession,
  detectAuthPersist,
  getAuthItem,
  persistAuthSession,
  setAuthItem,
} from '../shared/authStorage'

type IbAuthContextValue = {
  isAuthenticated: boolean
  subadmin: IbSubadmin | null
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => void
  refresh: () => Promise<void>
}

const IbAuthContext = createContext<IbAuthContextValue | null>(null)

function readStoredUser(): IbSubadmin | null {
  try {
    const raw = getAuthItem(IB_USER_KEY)
    if (!raw) return null
    return JSON.parse(raw) as IbSubadmin
  } catch {
    return null
  }
}

export function IbAuthProvider({ children }: { children: ReactNode }) {
  const [subadmin, setSubadmin] = useState<IbSubadmin | null>(() => readStoredUser())
  const [token, setToken] = useState(() => getAuthItem(IB_TOKEN_KEY) || '')

  useEffect(() => {
    if (!token) return
    const mode = detectAuthPersist(IB_TOKEN_KEY)
    void ibApi
      .me()
      .then((res) => {
        setSubadmin(res.subadmin)
        setAuthItem(IB_USER_KEY, JSON.stringify(res.subadmin), mode)
      })
      .catch(() => {
        clearAuthSession(IB_TOKEN_KEY, IB_USER_KEY)
        setToken('')
        setSubadmin(null)
      })
  }, [token])

  const value = useMemo<IbAuthContextValue>(
    () => ({
      isAuthenticated: Boolean(token),
      subadmin,
      async login(email, password, rememberMe = false) {
        const res = await ibApi.login(email, password)
        persistAuthSession({
          tokenKey: IB_TOKEN_KEY,
          userKey: IB_USER_KEY,
          token: res.accessToken,
          userJson: JSON.stringify(res.subadmin),
          rememberMe,
        })
        setToken(res.accessToken)
        setSubadmin(res.subadmin)
      },
      logout() {
        clearAuthSession(IB_TOKEN_KEY, IB_USER_KEY)
        setToken('')
        setSubadmin(null)
      },
      async refresh() {
        const res = await ibApi.me()
        setSubadmin(res.subadmin)
        setAuthItem(IB_USER_KEY, JSON.stringify(res.subadmin), detectAuthPersist(IB_TOKEN_KEY))
      },
    }),
    [subadmin, token],
  )

  return <IbAuthContext.Provider value={value}>{children}</IbAuthContext.Provider>
}

export function useIbAuth() {
  const ctx = useContext(IbAuthContext)
  if (!ctx) throw new Error('useIbAuth must be used within IbAuthProvider')
  return ctx
}
